// src/components/doctor/DoctorResultCard.jsx
import React from "react";
import Card from "@/components/ui/Card.jsx";
import { useNavigate } from "react-router-dom";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";
import {
  buildFavoriteDoctorIds,
  isFavoriteDoctor,
  setFavoriteDoctor,
  subscribeDoctorFavorites,
} from "@/services/doctorFavorites.js";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { formatYearsExperienceLabel } from "@/utils/experience.js";
import { resolveInsuranceDisplay } from "@/pages/appointments/insuranceVisibility.js";

const ICON_MEDICAL_SERVICES = "/icons/patient/medical_services_filled.svg";
const ICON_STAR_FILLED = "/icons/patient/star_filled.svg";
const ICON_FAVORITE_FILLED = "/icons/patient/favorite.svg";
const ICON_FAVORITE_OUTLINE = "/icons/patient/favorite_outline.svg";
const ICON_MAP_PIN_FILLED = "/icons/patient/distance_filled.svg";
const ICON_VERIFIED = "/icons/doctor/verified.svg";
const ICON_PENDING_VERIFICATION = "/icons/pending-verification.svg";
const ICON_EVISIT = "/icons/appointments/video_call.svg";
const AVATAR_FALLBACK = "/icons/doctor/avatar_placeholder.svg";

function normalizePriceLabel(value = "", currency = "") {
  const raw = String(value || "").trim();
  const code = String(currency || "").trim().toUpperCase();
  if (!raw) return { amountText: "", currencyText: code };
  if (!code) return { amountText: raw, currencyText: "" };

  const suffixPattern = new RegExp(`(?:\\s+${code})+$`, "i");
  if (suffixPattern.test(raw)) {
    return {
      amountText: raw.replace(suffixPattern, "").trim(),
      currencyText: code,
    };
  }

  return {
    amountText: raw,
    currencyText: code,
  };
}

function normalizePracticeMode(value = "") {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (["virtual_only", "virtual-only", "virtual", "evisit", "e-visit", "telehealth"].includes(raw)) {
    return "virtual_only";
  }
  if (["in_person_only", "in-person-only", "physical", "physical_practice", "inperson"].includes(raw)) {
    return "in_person_only";
  }
  if (raw === "hybrid") return "hybrid";
  return raw;
}

export default function DoctorResultCard({
  doctor,
  onLocate,
  onToggleFavorite,
  avatarSize = 68,
  favoriteReadOnly = false,
  ignoreStoredFavorites = false,
  showInsurance = false,
  naturalHeight = false,
}) {
  if (!doctor) return null;
  const { t } = useLanguage();

  const favoriteHydrated = doctor?.favoriteHydrated === true;
  const favoriteIds = React.useMemo(
    () => buildFavoriteDoctorIds(doctor),
    [doctor]
  );
  const primaryFavoriteId = favoriteIds[0] || "";
  const [favoriteVersion, setFavoriteVersion] = React.useState(0);
  React.useEffect(
    () => subscribeDoctorFavorites(() => setFavoriteVersion((value) => value + 1)),
    []
  );
  const favoriteFromStorage = React.useMemo(
    () => (ignoreStoredFavorites ? false : isFavoriteDoctor(favoriteIds)),
    [favoriteIds, favoriteVersion, ignoreStoredFavorites]
  );
  const explicitFavoriteState = favoriteHydrated ? doctor?.isFavorite === true : null;
  const [isFavorite, setIsFavorite] = React.useState(
    explicitFavoriteState !== null
      ? explicitFavoriteState
      : doctor?.isFavorite === true
        ? true
        : favoriteFromStorage
  );
  const doctorDisplayName = React.useMemo(
    () => resolvePatientFacingDoctorName(doctor, doctor?.name),
    [doctor]
  );
  const verificationStatus = normalizeVerificationStatus(
    doctor?.verificationStatus || doctor?.verified_status,
    doctor?.verified ? "verified" : ""
  );
  const showVerifiedBadge =
    verificationStatus === "verified" || verificationStatus === "pending";
  const practiceMode = normalizePracticeMode(doctor?.practice_mode || doctor?.practiceMode);
  const isVirtualOnly = practiceMode === "virtual_only";

  React.useEffect(() => {
    setIsFavorite(
      explicitFavoriteState !== null
        ? explicitFavoriteState
        : doctor?.isFavorite === true
          ? true
          : favoriteFromStorage
    );
  }, [doctor?.isFavorite, explicitFavoriteState, favoriteFromStorage]);

  const nav = useNavigate();
  const resolvedFacilityLat =
    doctor?.facility?.lat ??
    doctor?.facility?.latitude ??
    doctor?.facility?.facility_lat ??
    doctor?.facility?.facility_latitude ??
    doctor?.lat ??
    doctor?.latitude ??
    null;
  const resolvedFacilityLng =
    doctor?.facility?.lng ??
    doctor?.facility?.lon ??
    doctor?.facility?.longitude ??
    doctor?.facility?.facility_lng ??
    doctor?.facility?.facility_longitude ??
    doctor?.lng ??
    doctor?.lon ??
    doctor?.longitude ??
    null;
  const resolvedMapAddress =
    String(doctor?.mapAddress || "").trim() ||
    String(doctor?.address || "").trim() ||
    [
      doctor?.facility?.address,
      doctor?.facility?.city,
      doctor?.facility?.country,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(", ");
  const hasMapTarget = isVirtualOnly
    ? false
    : Number.isFinite(Number(resolvedFacilityLat)) &&
        Number.isFinite(Number(resolvedFacilityLng))
      ? true
      : Boolean(
          (resolvedMapAddress &&
            resolvedMapAddress !== t("patient.profile.fallback.addressNotProvided", "Address not provided")) ||
            String(doctor?.hospital || doctor?.facility?.name || "").trim() ||
            String(doctor?.facility?.city || "").trim() ||
            String(doctor?.facility?.country || "").trim() ||
            String(doctor?.displayLocation || "").trim()
        );

  const { amountText: servicePriceText, currencyText: serviceCurrencyText } =
    normalizePriceLabel(doctor?.serviceLabelPrice, doctor?.currency);
  const serviceLabelText =
    String(doctor?.serviceLabel || "").trim() ||
    t("appointments.discovery.serviceNotSet", "Service not set");
  const servicePriceDisplay = servicePriceText
    ? servicePriceText
    : t("appointments.discovery.priceNotSet", "Price not set");
  const yearsExperienceLabel = formatYearsExperienceLabel(doctor?.yearsExperience, t);
  const insuranceDisplay = React.useMemo(
    () =>
      resolveInsuranceDisplay(
        doctor?.insurancePolicy,
        doctor?.insurance_policy,
        doctor?.facility?.insurancePolicy,
        doctor?.facility?.insurance_policy
      ),
    [doctor]
  );

  return (
    <Card
      className={[
        "overflow-hidden w-full flex flex-col",
        naturalHeight ? "" : "h-full min-h-[318px]",
      ].join(" ")}
    >
      <div className="px-3 pt-3 pb-3">
        <div className="flex items-start gap-3">
          <img
            src={doctor.avatar || AVATAR_FALLBACK}
            alt={doctorDisplayName}
            className="afrimed-avatar-square shrink-0 object-cover"
            style={{ height: avatarSize, width: avatarSize }}
            fetchPriority="high"
            decoding="async"
            draggable="false"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = AVATAR_FALLBACK;
            }}
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <div className="text-title-medium text-on-surface truncate">
                {doctorDisplayName}
              </div>
              {showVerifiedBadge && (
                <span className="inline-flex h-5 w-5">
                  <img
                    src={
                      verificationStatus === "verified" && doctor.verifiedIcon
                        ? doctor.verifiedIcon
                        : verificationStatus === "verified"
                          ? ICON_VERIFIED
                          : ICON_PENDING_VERIFICATION
                    }
                    alt=""
                    className="h-full w-full object-contain"
                    draggable="false"
                  />
                </span>
              )}
            </div>

            <div className="text-body-medium text-on-surface-var truncate">
              {doctor.specialty}
            </div>

              <div className="mt-1 flex items-center gap-4 text-body-medium text-on-surface-var">
                <div className="flex items-center gap-1.5">
                  <span>{yearsExperienceLabel}</span>
                  <img
                    src={ICON_MEDICAL_SERVICES}
                    alt=""
                  className="h-[18px] w-[18px]"
                  draggable="false"
                />
              </div>

              <div className="flex items-center gap-1.5">
                <span>{doctor.rating}</span>
                <img
                  src={ICON_STAR_FILLED}
                  alt=""
                  className="h-[16px] w-[16px] shrink-0 translate-y-[1px]"
                  draggable="false"
                />
              </div>
            </div>
          </div>

          <button
            type="button"
            aria-label={
              isFavorite
                ? t("patient.profile.actions.removeFavorite", "Remove from favourites")
                : t("patient.profile.actions.addFavorite", "Add to favourites")
            }
            disabled={favoriteReadOnly}
            className={`h-11 w-11 flex items-center justify-center ${
              favoriteReadOnly ? "cursor-default" : ""
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (favoriteReadOnly) return;
              const nextValue = !isFavorite;
              setIsFavorite(nextValue);
              setFavoriteDoctor(favoriteIds, nextValue);
              onToggleFavorite?.(primaryFavoriteId || doctor?.doctorId || doctor?.id, nextValue);
            }}
          >
            <img
              src={isFavorite ? ICON_FAVORITE_FILLED : ICON_FAVORITE_OUTLINE}
              alt=""
              className={isFavorite ? "h-6 w-6 opacity-100" : "h-6 w-6 opacity-70"}
              draggable="false"
            />
          </button>
        </div>
      </div>

      <div className="h-px bg-[var(--Schemes-Outline-Variant,#D5D7DC)]" />

      <div className="px-3 py-3 space-y-3">
        {isVirtualOnly ? (
          <div className="flex items-center gap-2 text-body-large text-on-surface-var">
            <img src={ICON_EVISIT} alt="" className="h-6 w-6 shrink-0" draggable="false" />
            <span>{t("appointments.discovery.virtual", "Virtual")}</span>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-body-large text-on-surface-var break-words">
                  {doctor.hospital || "—"}
                </div>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  const payload = {
                    doctorId: doctor?.id ?? null,
                    facilityId: doctor?.facility_id ?? doctor?.facility?.id ?? null,
                    lat: resolvedFacilityLat,
                    lng: resolvedFacilityLng,
                    address: resolvedMapAddress,
                    name:
                      doctor?.facility?.name ??
                      doctor?.hospital ??
                      doctor?.displayLocation ??
                      doctor?.facility?.city ??
                      "",
                  };

                  if (typeof onLocate === "function") {
                    onLocate(payload);
                    return;
                  }

                  const qs = new URLSearchParams();
                  if (payload.facilityId) qs.set("facilityId", payload.facilityId);
                  if (payload.lat != null) {
                    qs.set("lat", payload.lat);
                    qs.set("facilityLat", payload.lat);
                  }
                  if (payload.lng != null) {
                    qs.set("lng", payload.lng);
                    qs.set("facilityLng", payload.lng);
                  }
                  if (payload.name) qs.set("facilityName", payload.name);

                  if (payload.address) qs.set("facilityAddress", payload.address);
                  nav(`/appointments/doctors?${qs.toString()}`);
                }}
                aria-label={t("common.map", "Map")}
                disabled={!hasMapTarget}
                className={`shrink-0 flex items-center gap-0 text-body-large ${
                  hasMapTarget ? "text-on-surface-var" : "text-on-surface-var opacity-60"
                }`}
              >
                <span>{t("common.map", "Map")}</span>
                <img src={ICON_MAP_PIN_FILLED} alt="" className="h-6 w-6" draggable="false" />
              </button>
            </div>

            <div className="mt-1 text-body-medium text-on-surface-var break-words whitespace-normal">
              {resolvedMapAddress ||
                t("patient.profile.fallback.addressNotProvided", "Address not provided")}
            </div>
          </div>
        )}

        {doctor.appointmentLabel ? (
          <div className="text-body-medium text-on-surface-var break-words">
            {doctor.appointmentLabel}
          </div>
        ) : null}

        <div className="flex items-start justify-between gap-2 pt-1">
          <div className="text-body-large text-on-surface-var">
            {serviceLabelText}
          </div>
          <div className="text-body-large text-on-surface-var whitespace-nowrap uppercase">
            {servicePriceDisplay}
            {servicePriceText && serviceCurrencyText ? (
              <>
                {" "}
                <span className="text-on-surface">{serviceCurrencyText}</span>
              </>
            ) : null}
          </div>
        </div>

        {showInsurance && (insuranceDisplay.hasActual || insuranceDisplay.generic) ? (
          <div className="pt-1">
            <div className="text-body-large text-on-surface-var">
              {t("appointments.doctor.insuranceAccepted", "Insurance accepted")}
            </div>
            {insuranceDisplay.hasActual ? (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {insuranceDisplay.companies.map((company) => (
                  <span
                    key={company}
                    className="inline-flex min-h-[28px] items-center rounded-lg bg-[var(--state-primary-12)] px-2.5 text-label-large text-on-surface"
                  >
                    {company}
                  </span>
                ))}
              </div>
            ) : (
              <div className="mt-1 text-body-medium text-on-surface-var">
                {t(
                  "appointments.doctor.insuranceGeneric",
                  "Accepting insurance — confirm provider during booking."
                )}
              </div>
            )}
          </div>
        ) : null}

        <div className="flex items-start justify-between gap-3 pt-1">
          <div className="text-body-large text-on-surface-var">
            {t("patient.profile.sections.languages", "Languages")}
          </div>
          <div className="max-w-[60%] text-right text-body-large text-on-surface-var break-words whitespace-normal">
            {doctor.languages || "—"}
          </div>
        </div>
      </div>
    </Card>
  );
}
